import { IDKeyTypes, ObjID } from './../../../model/id-manager';
import { AttributeOptionEditorDialogService } from './../../dialogs/attribute-option-editor-dialog/attribute-option-editor-dialog.service';
import { ModalMessagerService } from './../../dialogs/modal-messager/modal-messager.service';
import { AttributeConditionsDialogService } from './../../dialogs/attribute-conditions-dialog/attribute-conditions-dialog.service';
import { AvailCriteria, CostKeys, CalcInfo, SupplCalcInfo, CalcMethod, SupplCalcMethod, SupplCalcInput, SupplDefaultData } from './../../../types/project-config';
import { AnyHandler, SimpleHandler } from './../../../types/handlers';
import { StatefulCSS } from 'app/model/stateful_css';
import { AttributeInputType, ConfigAttribute, AttributeOption, CalcUtil } from 'app/types/project-config';
import { Component, OnInit, Input, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, Output, ElementRef } from '@angular/core';
import { Project } from 'app/model/project';

@Component({
  selector: 'app-attribute-workspace',
  templateUrl: './attribute-workspace.component.html',
  styleUrls: ['./attribute-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttributeWorkspaceComponent implements OnInit {

  private _ready = false;
  private _attribute?: ConfigAttribute;
  private typeStates = new StatefulCSS();
  @Input() set attribute(att: ConfigAttribute | undefined) {
    this._attribute = undefined;
    this.cdr.detectChanges();
    this._attribute = att;
    this.Refresh();
    if (!this._attribute) return;
    if (!(this._attribute.input)) {
      this.typeStates.SetState('');
      return;
    };
    if (this._attribute.input === 'checkbox') {
      this._defaultCheck = this._attribute.default === true;
    }
    if (this._attribute.input === 'input') {
      if (!!this._attribute.default && (typeof this._attribute.default === 'string')) {
        this._defaultText = this._attribute.default;
      }
    }
    if (this._attribute.input === 'qty') {
      if (!!this._attribute.default && (!Number.isNaN(Number(this._attribute.default)))) {
        this._defaultQty = Number(this._attribute.default);
      }
    }

    this._supplDefaults = undefined;
    if (this._attribute.input === 'selector') {
      this.defaultOption = this._attribute.default as ObjID;
    }
    this.calcMethod = this._attribute.calc;

    this.typeStates.SetState(this._attribute.input);
    this.Refresh();
  }
  get attribute(): ConfigAttribute | undefined {
    return this._attribute;
  }
  @Output()
  attributeChange = new EventEmitter<ConfigAttribute>();
  private _project?: Project;
  @Input() set project(proj: Project | undefined) {
    this._project = proj;
    this.Refresh();
  }
  get project(): Project | undefined {
    return this._project;
  }

  get name(): string {
    if (!this.attribute) return '';
    return this.attribute.label;
  }
  set name(name: string) {
    if (!this.attribute) {
      this.Defer(() => { this.name = name });
      return;
    }
    name.trim();
    this.attribute.label = name;
    this.Refresh();
  }
  get inputType(): AttributeInputType | '' {
    if (!this.attribute) return ''
    return this.attribute.input;
  }
  set inputType(type: AttributeInputType | '' | undefined) {
    if (!this.attribute) {
      this.Defer(() => { this.inputType = type; });
      return;
    };
    if (type === undefined) type = '';
    this.attribute.input = type as AttributeInputType;
    this.typeStates.SetState(type);
    this.Refresh();
  }
  set displayAlways(checked: boolean | undefined) {

    if (!this.attribute) {
      this.Defer(() => { this.displayAlways = checked; });
      return;
    }
    if (checked === undefined) checked = false;
    this.attribute.displayAlways = checked;
    this.cdr.markForCheck();
    this.Refresh();
  }
  get displayAlways(): boolean | undefined {
    if (!this.attribute) return undefined;
    return this.attribute.displayAlways === true;
  }
  set displayWhen(checked: boolean | undefined) {
    if (!this.attribute) {
      this.Defer(() => { this.displayWhen = checked; });
      return;
    };
    if (checked === undefined) {
      this.attribute.displayAlways = false;
      this.Refresh();
    }
  }
  get displayWhen(): boolean | undefined {
    if (!this.attribute) return undefined;
    return this.attribute.displayAlways === false;
  }
  get options(): Array<AttributeOption> {
    if (!this.attribute) return [];
    if (!this.attribute.options) return [];
    if (this.attribute.options.length > 1) {
      if (!this.isSelector) {
        return this.attribute.options.slice(0, 1);
      }
    }
    return this.attribute.options;
  }
  set options(opts: Array<AttributeOption>) {
    if (!this.attribute) {
      this.Defer(() => {
        this.options = opts;
      });
      return;
    };
    this.attribute.options = opts;
  }

  private _defaultOption?: ObjID;
  public set defaultOption(optID: ObjID | undefined) {
    if (optID === undefined) {
      if (!this.options) {
        this.Defer(() => { this.defaultOption = optID });
        return;
      }
      if (this.options && this.options.length) {
        optID = this.options[0].id;
      }
    }
    this._defaultOption = optID;
    if (this.attribute) {
      this.attribute.default = this._defaultOption;
    }
    this.Refresh();
  }
  public get defaultOption(): ObjID | undefined {
    return this._defaultOption;
  }

  get available(): Array<AvailCriteria> {
    if (!this.attribute) {
      return [];
    }
    if (!this.attribute.availability) {
      return [];
    }
    return this.attribute.availability;
  }
  get calcMethodTarget(): 'attributes' | 'suppl' | '' {
    if (!(this.project && this.attribute)) return '';
    const id = this.attribute.id;
    const type = this.project.GetFeatureType(id);
    if (!type) return '';
    return (type === 'suppl') ? 'suppl' : 'attributes';
  }

  private _calcMethod: CalcMethod | SupplCalcMethod = 'static';
  public set calcMethod(method: CalcMethod | SupplCalcMethod) {
    this._calcMethod = method;
    if (!this._ready) {
      this.Defer(() => {
        this.calcMethod = method;
      })
      return;
    }
    this.cdr.markForCheck();
    this.Refresh();
  }
  public get calcMethod(): CalcMethod | SupplCalcMethod {
    return this._calcMethod;
  }
  public get isSuppl(): boolean {
    if (!(this.project && this.attribute)) return false;
    const id = this.attribute.id;
    const featureType = this.project.GetFeatureType(id);
    if (featureType === false) return false;
    return (featureType === 'suppl');
  }
  public get isSelector(): boolean {
    return this.inputType === 'selector';
  }
  private _defaultCheck: boolean = false;
  public set defaultCheck(isChecked: boolean) {
    this._defaultCheck = isChecked === true;
    this.Refresh();
  }
  public get defaultCheck(): boolean {
    return this._defaultCheck;
  }
  public get defaultCheckLabel(): string {
    return this._defaultCheck ? 'checked' : 'unchecked';
  }

  private _defaultText: string = '';
  public set defaultText(text: string) {
    this._defaultText = text;
    this.Refresh();
  }
  public get defaultText(): string {
    return this._defaultText;
  }

  private _defaultQty: number = 0;
  public set defaultQty(val: number) {
    this._defaultQty = val;
    this.Refresh();
  }
  public get defaultQty():number {
    return this._defaultQty;
  }
  private _defaultQtyPerFt: number = 0;
  public set defaultQtyPerFt(val: number) {
    this._defaultQtyPerFt = val;
    this.Refresh();
  }
  public get defaultQtyPerFt():number {
    return this._defaultQty;
  }

  public get hasOptions(): boolean {
    if (!(this.project && this.attribute && this.attribute.options)) {
      return false;
    }
    return (this.attribute.options.length !== 0);
  }

  public get lengthBased():boolean {
    if(!this._attribute) return false;
    return this.project?.idManager.IDType(this._attribute.id) == IDKeyTypes.SegmentAttributes;
  }

  public get supplInputs(): Array<SupplCalcInput> {
    if (!(this.isSuppl && this.attribute && this.attribute.calc)) return [];
    const inputs = CalcUtil.GetSupplCalcInputs(this.attribute.calc);
    if (!inputs) return [];
    return inputs;
  }

  private _supplDefaults?: SupplDefaultData;
  public get supplDefaults(): SupplDefaultData {
    if (!(this.isSuppl && this.attribute && this.attribute.calc)) return {};
    if (!!this._supplDefaults) return this._supplDefaults;
    const defaults = CalcUtil.MergeSupplDefaults(this.attribute.calc, this.attribute.default);
    if (!defaults) return {};
    this._supplDefaults = defaults;
    return this._supplDefaults;
  }



  HandleSupplDefaultChange(_inputId: string, _inputValue: number) {
    if (!this.attribute) return;
    this.attribute.default = this.supplDefaults;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private domNode: ElementRef,
    private conditionSvc: AttributeConditionsDialogService,
    private optionSvc: AttributeOptionEditorDialogService,
    private messager: ModalMessagerService) { }

  ngOnInit(): void {
    this._ready = true;
    this.typeStates.SetPrefix('type');
    this.typeStates.AddState('');
    this.typeStates.AddState('qty');
    this.typeStates.AddState('qtyperft');
    this.typeStates.AddState('input');
    this.typeStates.AddState('selector');
    this.typeStates.AddState('checkbox');
    this.typeStates.SetMachine(this.domNode.nativeElement);
    this.typeStates.SetState('');
    this.project = this.project;

    this.cdr.detectChanges();
  }
  Refresh() {
    if (this._ready === true) {
      this.cdr.detectChanges();
    }
  }
  NotifyListeners() {
    this.attributeChange.emit(this._attribute);
  }
  Check(event: Event) {
    /*if (!(event.target && event.target)) return;
    const input: HTMLInputElement = event.target as HTMLInputElement;
    switch (input.value) {
      case '*':
        this.displayAlways = true;
        this.displayWhen = false;
        break;
      default:
        this.displayAlways = false;
        this.displayWhen = true;
        break;

    }*/
    this.Refresh();
    this.NotifyListeners();

  }
  TypeSelected() {
    this.Refresh();
    this.NotifyListeners();
  }
  Defer(handler: SimpleHandler) {
    if (!this.attribute) {
      window.requestAnimationFrame(() => {
        this.Defer(handler);
      });
      return;
    }
    handler();
  }
  GetConditionLabel(condition: AvailCriteria): string {
    if (!this.project) return '';
    return this.project.GetConditionLabel(condition);
  }
  AddCondition() {
    if (!this.project) return;
    this.conditionSvc.Show({
      project: this.project,
      attribute: this.attribute,
      criteria: undefined,
      handler: ((_criteria: AvailCriteria) => {
        this.Refresh();
      })
    })
    /*const ref: AvailCriteria = {
      id:this.idMgr.ID(availIdType),
      type: target,
      comparison:ComparisonKeys.Equals,
      attribute_id: lookup[target][availParts[0]].attribute,
    }*/
  }
  EditCondition(condition?: AvailCriteria) {
    if (!(this.project && this.attribute)) {
      return;
    }
    this.conditionSvc.Show({
      project: this.project,
      attribute: this.attribute,
      criteria: condition,
      handler: ((_criteria: AvailCriteria) => {
        this.Refresh();
      })
    });
  }
  RemoveCondition(condition: AvailCriteria) {
    this.messager.Confirm('You are about to remove an available-when condition. Press Ok to remove the condition, or Press Cancel to abort this action.',
      () => {
        if (!(this.project && this.attribute && this.attribute.availability)) return;
        if (this.attribute.availability.length === 0) return;
        this.attribute.availability.every((cond: AvailCriteria, index: number) => {
          if (!(this.project && this.attribute && this.attribute.availability)) return false;
          if (cond.id !== condition.id) return true;
          this.attribute.availability.splice(index, 1);
          this.project.idRegistry.UnRegisterID(cond.id);
          return false;
        });
        this.messager.Notify('Available-when condition removed. This change will only be local until you save changes to the server.', false);
        this.Refresh();
      }, () => { }, 'Remove available-when condition?');

  }
  RemoveAllConditions() {
    if (!(this.project && this.attribute && this.attribute.availability)) return;
    if (this.attribute.availability.length === 0) return;
    this.messager.Confirm('Removing all options for this attribute cannot be undone. Press Ok if you want to remove all entries. Otherwise press cancel to abort this action.',
      () => {
        this.messager.Progress('Getting ready ...', false, 0, 'Removing all options');
        if (!(this.project && this.attribute && this.attribute.availability)) return;
        const length = this.attribute.availability.length;
        this.attribute.availability.every((cond: AvailCriteria, index: number) => {
          if (!(this.project && this.attribute && this.attribute.availability)) return;
          this.messager.Progress(`Removing ${index} of ${length}`, false, index / length, 'Removing all options');
          this.project.idRegistry.UnRegisterID(cond.id);
          return true;
        });
        this.attribute.availability.splice(0, length);
        this.messager.Progress(`All options removed`, false, 1, 'Removing all options');
        this.Refresh();
      }, () => { }, 'Remove ALL options?')

  }
  GetOptionCost(option: AttributeOption, metric: string): string {
    if (!CostKeys[metric as CostKeys]) return Number(0).toPrecision(2);
    const costs = option.cost;
    if (!(costs && costs[metric as CostKeys])) return Number(0).toPrecision(2);
    return Number(costs[metric as CostKeys]).toFixed(2);
  }
  VerifyIncludeOption(option: AttributeOption) {
    if (option.id === 0) {
      return false;
    }
    /*if(option.label.toLowerCase().indexOf('select ') === 0) {
      return false;
    }*/
    return true;
  }
  AddAttributeOption() {
    if (!(this.project && this.attribute)) return;
    this.optionSvc.Show({
      project: this.project,
      attribute: this.attribute,
      handler: (option: AttributeOption | undefined) => {
        if (!this.isSelector) {
          if (!!option) {
            option.label = 'Value';
          }
          if (this.attribute && this.attribute.options) {
            this.attribute.options.splice(1, this.attribute.options.length - 2);
          }
        }
        this.cdr.markForCheck();
        this.Refresh();
      }
    });
  }
  EditNonSelOption() {
    if (!(this.project && this.attribute)) return;
    if (!this.isSelector) {
      if (!this.attribute.options) {
        this.attribute.options = [];

      }
      if (this.attribute.options.length === 0) {
        this.AddAttributeOption();
        return;
      } else {
        this.EditAttributeOption(this.attribute.options[0]);
      }
    }
  }
  EditAttributeOption(option: AttributeOption) {
    if (!(this.project && this.attribute)) return;
    this.optionSvc.Show({
      project: this.project,
      attribute: this.attribute,
      option: option,
      handler: (option: AttributeOption | undefined) => {
        if (!option) return;
        if (!this.isSelector) {
          option.label = 'Value';
          if (this.attribute && this.attribute.options) {
            this.attribute.options.splice(1, this.attribute.options.length - 2);
          }
        }
        this.cdr.markForCheck();
        this.Refresh();
      }
    });
  }
  DeleteAttributeOption(option: AttributeOption) {
    if (!(this.project && this.attribute)) return;
    this.messager.Confirm('Removing this attribute can not be undone, are you sure you want to continue?', () => {
      if (!(this.project && this.attribute && this.attribute.options && this.attribute.options.length)) return;
      this.attribute.options.every((value, index) => {
        if (!(this.project && this.attribute && this.attribute.options && this.attribute.options.length)) return;
        if (value.id === option.id) {
          this.attribute.options.splice(index, 1);
          this.project.idRegistry.UnRegisterID(option.id);
          this.cdr.markForCheck();
          this.cdr.detectChanges();
          return false;
        }
        return true;
      });

    }, () => {
      this.messager.Notify('Option deletion aborted');
    }, 'Deleting an option?');
  }
  DeleteAllOptions() {
    if (!(this.project && this.attribute)) return;
    this.messager.Confirm('You are about to remove all options for this attribute. This cannot be undone. Click ok to continue and delete all options. Or click cancel to abort this action.', () => {
      if (!(this.project && this.attribute && this.attribute.options && this.attribute.options.length)) return;
      for (const option of this.attribute.options) {
        this.project.idRegistry.UnRegisterID(option.id);
      }
      this.attribute.options.splice(0, this.attribute.options.length);
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, () => {
      this.messager.Notify('Delete request aborted');
    }, 'Delete ALL opttions?');
  }
  HandleMethodChange(method: CalcMethod | SupplCalcMethod) {
    if (!this.attribute) return;
    this.attribute.calc = method;
    this._calcMethod = method;
    this._supplDefaults = undefined;
    this.Refresh();
  }
}
